import React from "react";
import { KTMScreen, ActionEvent, RenderEvent, renderScreen } from "ktm-ui";
import { KTMApi } from "api/KTMApi";
import { Layout, Dialog, Button, Line, Card } from "ktm-ui";
import { CustomDropdown } from "components/CustomDropdown";

interface IdsPolicy {
  Id: string;
  Region1: string;
  Region1Rate: number;
  Region2: string;
  Region2Rate: number;
  Region3: string;
  Region3Rate: number;
}

const regionOptions = ["NONE", "REG1", "REG2", "REG3"];

export default KTMScreen(
  { name: "enroll.policy.edit-regions" },
  class extends React.Component<{ policy_id: string }> {
    data: {
      dsPolicy: IdsPolicy;
    };

    originalData: IdsPolicy | null = null;

    state = {
      selectedRegion1: "NONE" as string,
      region1Rate: "0",
      selectedRegion2: "NONE" as string,
      region2Rate: "0",
      selectedRegion3: "NONE" as string,
      region3Rate: "0",
    };

    render() {
      return renderScreen(this);
    }

    async start({ screen }: ActionEvent) {
      screen.defaultRecord = "@dsPolicy";
      await KTMApi.start(screen, {
        "@policy_id": this.props.policy_id,
      });
      screen.title = "Edit regions";
      this.setState({
        selectedRegion1: this.data.dsPolicy.Region1 ?? "NONE",
        region1Rate: String(this.data.dsPolicy.Region1Rate ?? 0),
        selectedRegion2: this.data.dsPolicy.Region2 ?? "NONE",
        region2Rate: String(this.data.dsPolicy.Region2Rate ?? 0),
        selectedRegion3: this.data.dsPolicy.Region3 ?? "NONE",
        region3Rate: String(this.data.dsPolicy.Region3Rate ?? 0),
      });
      this.originalData = { ...this.data.dsPolicy };
    }

    main({ draw }: RenderEvent) {
      return (
        <Layout>
          {draw(this.header)}
          {draw(this.regions_card)}
          {draw(this.footer)}
        </Layout>
      );
    }

    header() {
      return <Dialog.Header label="Edit Regions"></Dialog.Header>;
    }

    footer({ action }: RenderEvent) {
      const hasAnyRegion =
        this.state.selectedRegion1 !== "NONE" ||
        this.state.selectedRegion2 !== "NONE" ||
        this.state.selectedRegion3 !== "NONE";
      const rateSum =
        (this.state.selectedRegion1 !== "NONE" ? Number(this.state.region1Rate) || 0 : 0) +
        (this.state.selectedRegion2 !== "NONE" ? Number(this.state.region2Rate) || 0 : 0) +
        (this.state.selectedRegion3 !== "NONE" ? Number(this.state.region3Rate) || 0 : 0);
      const regionInvalid = hasAnyRegion && rateSum !== 100;

      return (
        <Dialog.Footer>
          <Button
            action={action(this.submit)}
            label="Save"
            size="large"
            style="primary"
            disable={regionInvalid}
          />
        </Dialog.Footer>
      );
    }

    regions_card() {
      return (
        <Card>
          <div className="policy-edit-card">
            {this.renderRegionSection()}
          </div>
        </Card>
      );
    }

    getAvailableRegions = (currentSlot: number) => {
      const selected = [
        this.state.selectedRegion1,
        this.state.selectedRegion2,
        this.state.selectedRegion3,
      ];
      return regionOptions.filter(
        (r) => r === "NONE" || r === selected[currentSlot] || !selected.includes(r)
      );
    };

    setRegion = (slot: number, value: string) => {
      if (slot === 0) {
        if (value === "NONE") {
          this.setState({ selectedRegion1: "NONE", region1Rate: "0", selectedRegion2: "NONE", region2Rate: "0", selectedRegion3: "NONE", region3Rate: "0" });
        } else {
          this.setState({ selectedRegion1: value });
        }
      } else if (slot === 1) {
        if (value === "NONE") {
          this.setState({ selectedRegion2: "NONE", region2Rate: "0", selectedRegion3: "NONE", region3Rate: "0" });
        } else {
          this.setState({ selectedRegion2: value });
        }
      } else {
        if (value === "NONE") {
          this.setState({ selectedRegion3: "NONE", region3Rate: "0" });
        } else {
          this.setState({ selectedRegion3: value });
        }
      }
    };

    setRegionRate = (slot: number, value: string) => {
      const next = value.replace(/^0+(?=\d)/, "");
      if (slot === 0) this.setState({ region1Rate: next });
      else if (slot === 1) this.setState({ region2Rate: next });
      else this.setState({ region3Rate: next });
    };

    blurRegionRate = (slot: number, value: string) => {
      const parsed = parseFloat(value);
      const next = value === "" || isNaN(parsed) ? "0" : String(Math.min(100, Math.max(0, parsed)));
      if (slot === 0) this.setState({ region1Rate: next });
      else if (slot === 1) this.setState({ region2Rate: next });
      else this.setState({ region3Rate: next });
    };

    renderRegionRow(slot: number, label: string, selectedRegion: string, rate: string) {
      return (
        <Line>
          <div className="RT-TextBox">
            <label>{label}</label>
            <CustomDropdown
              options={this.getAvailableRegions(slot)}
              selectedOption={selectedRegion}
              setSelectedOption={(v: string) => this.setRegion(slot, v)}
            />
          </div>
          {selectedRegion !== "NONE" ? (
            <div className="RT-TextBox">
              <label>Rate (%)</label>
              <div className="RT-TextBox__container">
                <input
                  className="RT-TextBox__input"
                  type="number"
                  value={rate}
                  onChange={(e) => this.setRegionRate(slot, e.target.value)}
                  onBlur={(e) => this.blurRegionRate(slot, e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div />
          )}
        </Line>
      );
    }

    renderRegionSection() {
      const { selectedRegion1, region1Rate, selectedRegion2, region2Rate, selectedRegion3, region3Rate } = this.state;
      const hasAnyRegion = selectedRegion1 !== "NONE" || selectedRegion2 !== "NONE" || selectedRegion3 !== "NONE";
      const rateSum =
        (selectedRegion1 !== "NONE" ? Number(region1Rate) || 0 : 0) +
        (selectedRegion2 !== "NONE" ? Number(region2Rate) || 0 : 0) +
        (selectedRegion3 !== "NONE" ? Number(region3Rate) || 0 : 0);

      return (
        <>
          {this.renderRegionRow(0, "Advisor Region", selectedRegion1, region1Rate)}
          {selectedRegion1 !== "NONE" && this.renderRegionRow(1, "Policy Region", selectedRegion2, region2Rate)}
          {selectedRegion2 !== "NONE" && this.renderRegionRow(2, "Region (Optional)", selectedRegion3, region3Rate)}
          {hasAnyRegion && (
            <div style={{ fontSize: 12, color: rateSum === 100 ? "green" : "red", marginTop: 4 }}>
              Rate total: {rateSum}% {rateSum !== 100 && "(must equal 100%)"}
            </div>
          )}
        </>
      );
    }

    getChangedFields(originalData, updatedData) {
      if (!originalData) return {};

      const changes = {};
      for (const key in updatedData) {
        if (updatedData[key] !== originalData[key]) {
          changes[key] = {
            oldValue:
              originalData[key] !== undefined ? originalData[key] : "N/A",
            newValue: updatedData[key] !== undefined ? updatedData[key] : "N/A",
          };
        }
      }
      return changes;
    }

    async submit({ screen }: ActionEvent) {
      this.data.dsPolicy.Region1 = this.state.selectedRegion1 === "NONE" ? null : this.state.selectedRegion1;
      this.data.dsPolicy.Region1Rate = this.state.selectedRegion1 === "NONE" ? 0 : Number(this.state.region1Rate) || 0;
      this.data.dsPolicy.Region2 = this.state.selectedRegion2 === "NONE" ? null : this.state.selectedRegion2;
      this.data.dsPolicy.Region2Rate = this.state.selectedRegion2 === "NONE" ? 0 : Number(this.state.region2Rate) || 0;
      this.data.dsPolicy.Region3 = this.state.selectedRegion3 === "NONE" ? null : this.state.selectedRegion3;
      this.data.dsPolicy.Region3Rate = this.state.selectedRegion3 === "NONE" ? 0 : Number(this.state.region3Rate) || 0;

      const changes = this.getChangedFields(
        this.originalData,
        this.data.dsPolicy
      );

      const logDetails = Object.entries(changes)
        .map(([key, value]) => {
          const change = value as { oldValue: any; newValue: any };
          return `${key}: ${change.oldValue} → ${change.newValue}`;
        })
        .join(" | ");

      const policyLogs = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Note: logDetails,
          PolicyId: this.props.policy_id,
          Action: "Policy Update Regions",
        }),
      };

      await KTMApi.action(screen, "submit", {
        "@dsPolicy.Region1": this.data.dsPolicy.Region1,
        "@dsPolicy.Region1Rate": this.data.dsPolicy.Region1Rate,
        "@dsPolicy.Region2": this.data.dsPolicy.Region2,
        "@dsPolicy.Region2Rate": this.data.dsPolicy.Region2Rate,
        "@dsPolicy.Region3": this.data.dsPolicy.Region3,
        "@dsPolicy.Region3Rate": this.data.dsPolicy.Region3Rate,
        "@policy_id": this.props.policy_id,
      }).then(() => {
        fetch("/api/PlanAdmin/updatepolicylogs", policyLogs);
      });
      screen.recordChanged("updated", this.props.policy_id);
      screen.close({ continue: true });
    }
  }
);
